import React, { CSSProperties, FC, memo } from "react"
import { NewBookingInput, OptionalInputFieldsInput } from "../../../../generated/graphql"
import type { NewBookingInputKeys } from "./bookingInputTypes"
import { Checkbox, Form, Input, Select } from "antd"
import css from "../StepSummary2.module.css"
import ReactMarkdown from "react-markdown"
import classNames from "classnames"
import CustomInputCheckboxGroup from "./CustomInputCheckboxGroup"
import CustomInputRadioGroup from "./CustomInputRadioGroup"

type CustomInputProps = {
    field: OptionalInputFieldsInput
    bookingData?: NewBookingInput
    additionalInfoJSON: any
    setAdditionalInfoJSON: (value: any) => void
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    style?: CSSProperties
    handleInputFieldValues?: (values: Array<{ [Key: string]: string | boolean | undefined }>) => void
    showAlert?: boolean
}

const CustomInput: FC<CustomInputProps> = ({
    field,
    bookingData,
    style,
    additionalInfoJSON,
    setAdditionalInfoJSON,
    updateBookingData,
    handleInputFieldValues,
    showAlert,
}) => {
    const fieldType = field.type

    const updateCustomFieldValues = (selectedValue?: string | boolean) => {
        const updatedAdditionalInfoJSON = { ...additionalInfoJSON }
        const isRequiredCheckbox = field.type === "Checkbox" && field.required

        if (field.label) {
            if (!selectedValue) {
                delete updatedAdditionalInfoJSON[field.label]
            } else if (field.label && selectedValue) {
                updatedAdditionalInfoJSON[field.label] = selectedValue
            }

            // Only save checkbox if it is not required as it would be the same value every time
            if (!isRequiredCheckbox) {
                updateBookingData("additionalInfo", JSON.stringify(updatedAdditionalInfoJSON))
            }

            updateBookingData("additionalInfo", JSON.stringify(updatedAdditionalInfoJSON))
            setAdditionalInfoJSON(updatedAdditionalInfoJSON)
            handleInputFieldValues?.([{ [field.name ?? field.label]: selectedValue }])
        }
    }

    const RenderDescription = ({ customStyle }: { customStyle?: React.CSSProperties }) => {
        const processMarkdown = (input: string) => {
            return input.replace(/(.*)\n---/g, "$1\n\n---\n\n")
        }

        return (
            <Form.Item style={{ ...style, ...customStyle }} key={field.name}>
                <ReactMarkdown skipHtml={false} className={classNames(css.markdown, css.inputDescription)}>
                    {processMarkdown(field.description ?? "")}
                </ReactMarkdown>
            </Form.Item>
        )
    }

    // const RenderDescription = () => {
    //     const findMarkDownLink = field.description?.match(/\[(.*?)\]\((.*?)\)/)
    //     const markDownLink = findMarkDownLink ? findMarkDownLink : null

    //     if (markDownLink) {
    //         const descriptionWithLink = field.description?.replace(markDownLink[0], "")

    //         return (
    //             <div className={css.inputDescription}>
    //                 {descriptionWithLink}
    //                 <a className={css.link} href={markDownLink[2]} target='_blank' rel='noopener noreferrer'>
    //                     {markDownLink[1]}
    //                 </a>
    //             </div>
    //         )
    //     }

    //     return (
    //         <Form.Item /* label={field.label} */ style={style} key={field.name}>
    //             <div className={css.inputDescription}>{field.description}</div>
    //         </Form.Item>
    //     )
    // }

    if (fieldType === "Text") {
        return (
            <Form.Item label={field.label} style={style} key={field.name}>
                <Input.TextArea
                    onChange={(e) => updateCustomFieldValues(e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    maxLength={64}
                    showCount
                    style={{ fontSize: "11px" }}
                />
                <RenderDescription />
            </Form.Item>
        )
    }

    if (fieldType === "Checkbox") {
        return (
            <Form.Item style={style} key={field.name}>
                <Checkbox
                    onChange={(e) => updateCustomFieldValues(e.target.checked)}
                    checked={additionalInfoJSON[field.label ?? ""]}
                >
                    <div>{field.label}</div>
                </Checkbox>
                <RenderDescription customStyle={{ paddingLeft: 18 }} />
            </Form.Item>
        )
    }

    if (fieldType === "CheckboxGroup") {
        return (
            <CustomInputCheckboxGroup
                field={field}
                bookingData={bookingData}
                additionalInfoJSON={additionalInfoJSON}
                setAdditionalInfoJSON={setAdditionalInfoJSON}
                updateBookingData={updateBookingData}
                style={style}
                handleInputFieldValues={handleInputFieldValues}
                showAlert={showAlert}
            />
        )
    }

    if (fieldType === "RadioGroup") {
        return (
            <CustomInputRadioGroup
                field={field}
                bookingData={bookingData}
                additionalInfoJSON={additionalInfoJSON}
                setAdditionalInfoJSON={setAdditionalInfoJSON}
                updateBookingData={updateBookingData}
                style={style}
                handleInputFieldValues={handleInputFieldValues}
                showAlert={showAlert}
            />
        )
    }

    return (
        <Form.Item label={field.label} style={style} key={field.name}>
            <Select
                onSelect={updateCustomFieldValues}
                value={additionalInfoJSON[field.label ?? ""]}
                placeholder={field.placeholder}
                style={{ width: "100%" }}
                allowClear
                onClear={() => updateCustomFieldValues()}
            >
                <Select.Option key={null} value={null}>
                    {field.placeholder}
                </Select.Option>
                {field.options?.map((option) => {
                    return (
                        <Select.Option key={option.label} value={option.label}>
                            {option.label}
                        </Select.Option>
                    )
                })}
            </Select>
            <RenderDescription />
        </Form.Item>
    )
}

export default memo(CustomInput)
